import { supabase } from "@/integrations/supabase/client";

const PRODUCT_GIFT_SETTINGS_KEY = "product_gift_links";

type ProductGiftConfig = {
  gift_product_id: string;
  gift_label?: string | null;
};

const normalizeGiftMap = (value: unknown): Record<string, ProductGiftConfig> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, ProductGiftConfig>>((acc, [productId, config]) => {
    if (!config || typeof config !== "object" || Array.isArray(config)) return acc;
    const giftProductId = (config as ProductGiftConfig).gift_product_id;
    if (!giftProductId || typeof giftProductId !== "string") return acc;

    acc[productId] = {
      gift_product_id: giftProductId,
      gift_label: typeof (config as ProductGiftConfig).gift_label === "string"
        ? (config as ProductGiftConfig).gift_label
        : null,
    };
    return acc;
  }, {});
};

export const getGiftLink = async (productId: string): Promise<ProductGiftConfig | null> => {
  const { data, error } = await supabase
    .from("store_settings")
    .select("value")
    .eq("key", PRODUCT_GIFT_SETTINGS_KEY)
    .maybeSingle();

  if (error) {
    console.warn("Failed to read gift settings fallback:", error);
    return null;
  }

  const map = normalizeGiftMap(data?.value);
  return map[productId] || null;
};

export const saveGiftLink = async (productId: string, config: ProductGiftConfig | null) => {
  const { data, error } = await supabase
    .from("store_settings")
    .select("id, value")
    .eq("key", PRODUCT_GIFT_SETTINGS_KEY)
    .maybeSingle();

  if (error) throw error;

  const currentMap = normalizeGiftMap(data?.value);

  if (config?.gift_product_id) {
    currentMap[productId] = {
      gift_product_id: config.gift_product_id,
      gift_label: config.gift_label?.trim() || null,
    };
  } else {
    delete currentMap[productId];
  }

  if (data?.id) {
    const { error: updateError } = await supabase
      .from("store_settings")
      .update({ value: currentMap as any, updated_at: new Date().toISOString() })
      .eq("id", data.id);

    if (updateError) throw updateError;
    return;
  }

  const { error: insertError } = await supabase
    .from("store_settings")
    .insert({ key: PRODUCT_GIFT_SETTINGS_KEY, value: currentMap as any });

  if (insertError) throw insertError;
};